import React, { useState, useEffect, useRef } from "react";
import { Input, Checkbox, Button, Spin, Radio } from "antd";
import { SearchOutlined, DownOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getGroupProducts } from "../../API  Functions/productGroupFunctions";
import LoadingSpinner from "../../components/Loading";

// Small helper to build a fresh, empty sub-group row
const makeEmptySubGroup = () => ({
  key: `sg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  englishName: "",
  arabicName: "",
  searchQuery: "",
  products: [],
  selectedProducts: [],
  loading: false,
  showDropdown: false,
});

const NewGroup_Modal = ({
  open,
  onClose,
  onSave,
  editData,
  onUpdateGroup,
  groupLoading,
}) => {
  const { t, i18n } = useTranslation();
  const [searchByShortCode, setSearchByShortCode] = useState(false);

  const [englishName, setEnglishName] = useState("");
  const [arabicName, setArabicName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [groupFilter, setGroupFilter] = useState("groups");

  // ---- NEW: group "mode" -------------------------------------------------
  // "standalone"    -> normal group, just name + product_ids
  // "withSubGroups" -> parent group created together with a batch of new sub-groups
  // "asSubGroup"    -> this new group becomes a sub-group under an existing parent
  const [groupMode, setGroupMode] = useState("standalone");

  // ---- NEW: sub-groups being authored inline -----------------------------
  const [subGroups, setSubGroups] = useState([]); // array of makeEmptySubGroup() shaped objects

  // ---- NEW: parent-group picker (for "asSubGroup" mode, and for editing) --
  const [parentSearchQuery, setParentSearchQuery] = useState("");
  const [parentOptions, setParentOptions] = useState([]);
  const [parentLoading, setParentLoading] = useState(false);
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null); // { id, name, name_ar }
  const [removeParent, setRemoveParent] = useState(false); // edit mode: user wants to detach from current parent

  const company_id = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );
  const user_id = useSelector((state) => state?.login?.user?.result?.user_id);

  const userData = useSelector((state) => state?.login?.user?.result);

  const dropdownRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const parentDropdownRef = useRef(null);
  const parentToggleBtnRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {

       if (modalRef.current?.contains(event.target)) {
      return;
    }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        toggleBtnRef.current &&
        !toggleBtnRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
      if (
        parentDropdownRef.current &&
        !parentDropdownRef.current.contains(event.target) &&
        parentToggleBtnRef.current &&
        !parentToggleBtnRef.current.contains(event.target)
      ) {
        setShowParentDropdown(false);
      }
    };

    if (showDropdown || showParentDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, showParentDropdown]);

  // Fetch products (top-level / standalone group product picker)
  const getProducts = async (val = "") => {
    setLoading(true);
    try {
      if (searchByShortCode) {
        let payload = {
          lang: i18n.language == "ar" ? "ar_001" : "en_US",
          short_code: val,
          type: "all", //required // all or low or high or out
          company_id: userData?.company_id,
        };
        const response = await axiosInstance.post(
          URLS.GET_STOCK_BY_CODE,
          payload,
        );
        if (response?.data?.result[0]?.response) {
          setProducts(response?.data?.result[0].response);
        }
      } else {
        const payload = {
          user_id: userData?.user_id,
          company_id: userData?.company_id,
          category_id: false,
          product_name: val,
          limit: 10,
          offset: 0,
          lang: i18n.language === "ar" ? "ar_001" : "en_US",
        };
        const res = await axiosInstance.post(URLS.GET_ALL_PRODUCTS, payload);

        const result = res?.data?.result[0]?.response || [];
        setProducts(result);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ---- NEW: product search reused for any sub-group row ------------------
  const getSubGroupProducts = async (key, val = "") => {
    setSubGroups((prev) =>
      prev.map((sg) => (sg.key === key ? { ...sg, loading: true } : sg)),
    );
    try {
      const payload = {
        user_id: userData?.user_id,
        company_id: userData?.company_id,
        category_id: false,
        product_name: val,
        limit: 10,
        offset: 0,
        lang: i18n.language === "ar" ? "ar_001" : "en_US",
      };
      const res = await axiosInstance.post(URLS.GET_ALL_PRODUCTS, payload);
      const result = res?.data?.result[0]?.response || [];
      setSubGroups((prev) =>
        prev.map((sg) =>
          sg.key === key ? { ...sg, products: result, loading: false } : sg,
        ),
      );
    } catch (err) {
      console.error("Error fetching sub-group products:", err);
      setSubGroups((prev) =>
        prev.map((sg) =>
          sg.key === key ? { ...sg, products: [], loading: false } : sg,
        ),
      );
    }
  };

  // ---- NEW: fetch parent group options (GetParentProductGroup) -----------
  const getParentGroups = async (val = "") => {
    setParentLoading(true);
    try {
      const payload = {
        user_id: userData?.user_id,
        company_id: userData?.company_id,
        search: val,
      };
      const res = await axiosInstance.post(
        URLS.GET_PARENT_PRODUCT_GROUP,
        payload,
      );
      const result =
        res?.data?.result?.[0]?.product_groups ||
        res?.data?.result?.[0]?.response ||
        [];
      setParentOptions(result);
    } catch (err) {
      console.error("Error fetching parent groups:", err);
      setParentOptions([]);
    } finally {
      setParentLoading(false);
    }
  };

  const getProductsofGroup = async () => {
    try {
      const payload = {
        lang: i18n.language === "ar" ? "ar_001" : "en_US",
        company_id: company_id,
        user_id: user_id,
        group_id: editData.id,
      };
      const res = await getGroupProducts(payload);

      if (res?.result[0]?.status_code === 200) {
        setSelectedProducts(res?.result[0]?.product_list || []);
      }
    } catch (error) {
      console.error("Error fetching order lines:", error);
    } finally {
    }
  };

  useEffect(() => {
    if (editData) {
      getProductsofGroup();
      setEnglishName(editData?.name);
      setArabicName(editData?.name_ar);
      // Pre-fill parent info if this group is already a sub-group
      if (editData?.parent_id) {
        setSelectedParent({
          id: editData.parent_id,
          name: editData?.parent_name,
          name_ar: editData?.parent_name_ar,
        });
        setGroupMode("asSubGroup");
      }
    }
  }, [editData]);

  // Debounced search - standalone product list
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length > 1) getProducts(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounced search - parent group picker
  useEffect(() => {
    const timer = setTimeout(() => {
      if (groupMode === "asSubGroup") getParentGroups(parentSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [parentSearchQuery, groupMode]);

  const toggleProduct = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  // ---- NEW: sub-group row helpers ----------------------------------------
  const addSubGroup = () => {
    setSubGroups((prev) => [...prev, makeEmptySubGroup()]);
  };

  const removeSubGroup = (key) => {
    setSubGroups((prev) => prev.filter((sg) => sg.key !== key));
  };

  const updateSubGroupField = (key, field, value) => {
    setSubGroups((prev) =>
      prev.map((sg) => (sg.key === key ? { ...sg, [field]: value } : sg)),
    );
  };

  const toggleSubGroupProduct = (key, prodId) => {
    setSubGroups((prev) =>
      prev.map((sg) => {
        if (sg.key !== key) return sg;
        const already = sg.selectedProducts.includes(prodId);
        return {
          ...sg,
          selectedProducts: already
            ? sg.selectedProducts.filter((pid) => pid !== prodId)
            : [...sg.selectedProducts, prodId],
        };
      }),
    );
  };

  // Debounced search per sub-group row
  useEffect(() => {
    const timers = subGroups.map((sg) =>
      setTimeout(() => {
        if (sg.searchQuery.length > 1) getSubGroupProducts(sg.key, sg.searchQuery);
      }, 300),
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subGroups.map((sg) => sg.searchQuery).join("|")]);

  const handleClose = () => {
    setEnglishName("");
    setArabicName("");
    setSearchQuery("");
    setSelectedProducts([]);
    setShowDropdown(false);
    setGroupMode("standalone");
    setSubGroups([]);
    setSelectedParent(null);
    setParentSearchQuery("");
    setRemoveParent(false);
    onClose?.();
  };

  const handleSave = () => {
    if (englishName.trim() === "" || arabicName.trim() === "") {
      toast.error(t("Please enter both English and Arabic group names."));
      return;
    }

    if (groupMode === "withSubGroups") {
      if (subGroups.length === 0) {
        toast.error(t("Please add at least one sub-group."));
        return;
      }
      const invalid = subGroups.some(
        (sg) => sg.englishName.trim() === "" || sg.arabicName.trim() === "",
      );
      if (invalid) {
        toast.error(
          t("Please enter both English and Arabic names for every sub-group."),
        );
        return;
      }
    }

    if (groupMode === "asSubGroup" && !selectedParent) {
      toast.error(t("Please select a parent group."));
      return;
    }

    if (editData) {
      // Edit flow: name/product changes go through onUpdateGroup as before.
      // Parent assignment/removal is passed alongside so the caller can
      // include parent_id / remove_parent_id in the EditProductGroup payload.
      onUpdateGroup(
        englishName,
        arabicName,
        selectedProducts,
        editData?.id,
        {
          parentId: groupMode === "asSubGroup" ? selectedParent?.id : undefined,
          removeParentId: removeParent ? editData?.parent_id : undefined,
        },
      );
      return;
    }

    if (groupMode === "withSubGroups") {
      onSave?.({
        englishName,
        arabicName,
        isCreateSubGroup: true,
        subGroupsVals: subGroups.map((sg) => ({
          name: sg.englishName.trim(),
          name_ar: sg.arabicName.trim(),
          product_ids: sg.selectedProducts,
        })),
      });
      return;
    }

    if (groupMode === "asSubGroup") {
      onSave?.({
        englishName,
        arabicName,
        parentId: selectedParent?.id,
        selectedProducts,
      });
      return;
    }

    // standalone
    onSave?.({
      englishName,
      arabicName,
      selectedProducts,
    });
  };

  if (!open) return null; // hide modal when closed

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        backdropFilter: "blur(10px)",
        backgroundColor: "rgba(0,0,0,0.4)",
      }}
    >
      {groupLoading && <LoadingSpinner />}
      {/* Modal container */}
      <div ref={modalRef} className="relative w-[600px] max-h-[80vh] overflow-y-auto custom-scrollbar bg-white rounded-[20px] p-6 shadow-2xl animate-fadeIn">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className={`absolute top-4 ${i18n.language === "ar" ? "left-4" : "right-4"} text-gray-400 hover:text-black text-2xl font-semibold`}
        >
          ×
        </button>

        {/* Title */}
        <h2 className="text-lg font-bold mb-4">
          {editData ? t("Edit Product Group") : t("Create New Group")}
        </h2>

        {/* Group Name Section */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-2 text-gray-600">
            {t("Group Name")}
          </h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-xs mb-2 font-medium text-gray-600">
                {t("English")}
              </label>
              <Input
                size="small"
                placeholder={t("Enter group name")}
                value={englishName}
                onChange={(e) => {
                  const val = e.target.value.replace(
                    /[^\u0600-\u06FFa-zA-Z0-9\s]/g,
                    "",
                  );
                  setEnglishName(val.trimStart());
                }}
                maxLength={20}
                onBlur={() => setEnglishName((prev) => prev.trim())}
                className="rounded-md h-8 text-xs"
              />
            </div>
            <div>
              <label className="block text-xs mb-2 font-medium text-gray-600">
                {t("Arabic")}
              </label>
              <Input
                size="small"
                placeholder="أدخل اسم المجموعة"
                value={arabicName}
                onChange={(e) => {
                  const val = e.target.value.replace(
                    /[^\u0600-\u06FFa-zA-Z0-9\s]/g,
                    "",
                  );
                  setArabicName(val.trimStart());
                }}
                maxLength={20}
                onBlur={() => setArabicName((prev) => prev.trim())}
                dir="rtl"
                className="rounded-md h-8 text-xs"
              />
            </div>
          </div>
        </div>

        {/* ---- NEW: Group type selector --------------------------------- */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-2 text-gray-600">
            {t("Group Type")}
          </h3>
          <Radio.Group
            value={groupMode}
            onChange={(e) => setGroupMode(e.target.value)}
            className="flex flex-col gap-2 text-xs"
          >
            <Radio value="standalone">{t("Standalone group")}</Radio>
            <Radio value="withSubGroups">
              {t("Create with sub-groups")}
            </Radio>
            <Radio value="asSubGroup">
              {t("Make this a sub-group of an existing group")}
            </Radio>
          </Radio.Group>
        </div>

        {/* ---- NEW: Parent group picker (asSubGroup mode) ---------------- */}
        {groupMode === "asSubGroup" && (
          <div className="mb-6 relative">
            <label className="block text-sm mb-2 font-semibold text-gray-600">
              {t("Parent Group")}
            </label>
            <button
              ref={parentToggleBtnRef}
              onClick={() => setShowParentDropdown(!showParentDropdown)}
              className="w-full flex items-center justify-between border border-gray-300 rounded-md h-8 px-4 text-xs focus:outline-none"
            >
              <span>
                {selectedParent
                  ? i18n.language === "ar"
                    ? selectedParent.name_ar || selectedParent.name
                    : selectedParent.name
                  : t("Select parent group")}
              </span>
              <DownOutlined
                className={`transition-transform ${showParentDropdown ? "rotate-180" : ""}`}
              />
            </button>

            {showParentDropdown && (
              <div
                ref={parentDropdownRef}
                className="absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg w-full mt-2 overflow-y-auto p-2 custom-scrollbar"
                style={{ maxHeight: "200px" }}
              >
                <Input
                  prefix={<SearchOutlined />}
                  placeholder={t("Search groups...")}
                  value={parentSearchQuery}
                  onChange={(e) => setParentSearchQuery(e.target.value)}
                  className="mb-2 text-xs h-8 rounded-md focus:outline-none"
                  suffix={
                    parentSearchQuery && (
                      <span
                        onClick={() => setParentSearchQuery("")}
                        className="cursor-pointer text-gray-400 hover:text-gray-600 text-xs font-semibold"
                      >
                        ×
                      </span>
                    )
                  }
                />
                {parentLoading ? (
                  <div className="flex justify-center text-xs py-2">
                    <Spin />
                  </div>
                ) : parentOptions.length > 0 ? (
                  parentOptions.map((g) => (
                    <div
                      key={g.id}
                      onClick={() => {
                        setSelectedParent(g);
                        setShowParentDropdown(false);
                        setRemoveParent(false);
                      }}
                      className={`flex items-center py-1 px-2 rounded-lg cursor-pointer hover:bg-green-50 ${
                        selectedParent?.id === g.id ? "bg-green-50" : ""
                      }`}
                    >
                      <span className="text-gray-800 text-xs">
                        {i18n.language === "ar" ? g.name_ar || g.name : g.name}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-400 py-4 text-xs">
                    {t("No groups found")}
                  </p>
                )}
              </div>
            )}

            {editData?.parent_id && (
              <div className="mt-2">
                <Checkbox
                  checked={removeParent}
                  onChange={(e) => {
                    setRemoveParent(e.target.checked);
                    if (e.target.checked) setSelectedParent(null);
                  }}
                >
                  <span className="text-xs text-gray-600">
                    {t("Remove from current parent group")}
                  </span>
                </Checkbox>
              </div>
            )}
          </div>
        )}

        {/* Product Dropdown Section - hidden while authoring sub-groups,   */}
        {/* since products live inside each sub-group instead of the parent */}
        {groupMode !== "withSubGroups" && (
          <div className="mb-8 relative">
            <label className="block text-sm mb-2 font-semibold text-gray-600">
              {t("Product List")}
            </label>

            <button
              ref={toggleBtnRef}
              onClick={() => setShowDropdown(!showDropdown)}
              className="w-full flex items-center justify-between border border-gray-300 rounded-md h-8 px-4 text-xs focus:outline-none"
            >
              <span>
                {selectedProducts.length > 0
                  ? `${selectedProducts.length} ${t("selected")}`
                  : t("Select products")}
              </span>
              <DownOutlined
                className={`transition-transform ${
                  showDropdown ? "rotate-180" : ""
                }`}
              />
            </button>

            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg w-full mt-2 overflow-y-auto p-2 custom-scrollbar"
                style={{ maxHeight: "200px" }}
              >
                <div className="flex gap-x-2">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder={t("Search products...")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mb-2 text-xs h-8 rounded-md focus:outline-none"
                    suffix={
                      searchQuery && (
                        <span
                          onClick={() => setSearchQuery("")}
                          className="cursor-pointer text-gray-400 hover:text-gray-600 text-xs font-semibold"
                        >
                          ×
                        </span>
                      )
                    }
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (searchByShortCode) {
                        setSearchQuery("");
                      }
                      setSearchByShortCode(!searchByShortCode);
                    }}
                    className={`h-8 px-2 rounded-md border text-xs whitespace-nowrap
    ${
      searchByShortCode
        ? "bg-blue-100 border-primaryBlue text-primaryBlue"
        : "bg-white border-gray-300 text-gray-700"
    }`}
                  >
                    {t("Search Code")}
                  </button>
                </div>
                {loading ? (
                  <div className="flex justify-center text-xs py-2">
                    <Spin />
                  </div>
                ) : products.length > 0 && searchQuery.length > 1 ? (
                  <>
                    {products.map((p) => {
                      const id = p.quant_id ?? p.prod_id ?? p.id;
                      const isSelected = selectedProducts.includes(id);

                      return (
                        <div
                          key={id}
                          onClick={() => {
                            toggleProduct(id);
                          }}
                          className={`flex items-center py-1 px-2 rounded-lg cursor-pointer hover:bg-green-50 ${
                            isSelected ? "bg-green-50" : ""
                          }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleProduct(id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="ms-2 text-gray-800 text-xs">
                              {p.product_name2 && p.product_name2 != ""
                                ? p.product_name2
                                : p.product_name}
                            </span>
                          </Checkbox>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {searchQuery.length > 1 && (
                      <p className="text-center text-gray-400 py-4">
                        {t("No products found")}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ---- NEW: Sub-groups authoring section ------------------------ */}
        {groupMode === "withSubGroups" && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-600">
                {t("Sub Groups")}
              </label>
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={addSubGroup}
                className="text-xs"
              >
                {t("Add Sub Group")}
              </Button>
            </div>

            {subGroups.length === 0 && (
              <p className="text-center text-gray-400 text-xs py-4 border border-dashed border-gray-300 rounded-md">
                {t('Click "Add Sub Group" to start.')}
              </p>
            )}

            <div className="flex flex-col gap-4">
              {subGroups.map((sg, idx) => (
                <div
                  key={sg.key}
                  className="border border-gray-200 rounded-md p-3 relative"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600">
                      {t("Sub Group")} {idx + 1}
                    </span>
                    <DeleteOutlined
                      className="text-red-400 hover:text-red-600 cursor-pointer text-xs"
                      onClick={() => removeSubGroup(sg.key)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <Input
                      size="small"
                      placeholder={t("English Name")}
                      value={sg.englishName}
                      onChange={(e) => {
                        const val = e.target.value.replace(
                          /[^\u0600-\u06FFa-zA-Z0-9\s]/g,
                          "",
                        );
                        updateSubGroupField(sg.key, "englishName", val.trimStart());
                      }}
                      maxLength={20}
                      className="rounded-md h-8 text-xs"
                    />
                    <Input
                      size="small"
                      placeholder="أدخل اسم المجموعة الفرعية"
                      value={sg.arabicName}
                      onChange={(e) => {
                        const val = e.target.value.replace(
                          /[^\u0600-\u06FFa-zA-Z0-9\s]/g,
                          "",
                        );
                        updateSubGroupField(sg.key, "arabicName", val.trimStart());
                      }}
                      maxLength={20}
                      dir="rtl"
                      className="rounded-md h-8 text-xs"
                    />
                  </div>

                  <div className="relative">
                    <button
                      onClick={() =>
                        updateSubGroupField(sg.key, "showDropdown", !sg.showDropdown)
                      }
                      className="w-full flex items-center justify-between border border-gray-300 rounded-md h-8 px-3 text-xs focus:outline-none"
                    >
                      <span>
                        {sg.selectedProducts.length > 0
                          ? `${sg.selectedProducts.length} ${t("selected")}`
                          : t("Select products")}
                      </span>
                      <DownOutlined
                        className={`transition-transform ${sg.showDropdown ? "rotate-180" : ""}`}
                      />
                    </button>

                    {sg.showDropdown && (
                      <div
                        className="absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg w-full mt-2 overflow-y-auto p-2 custom-scrollbar"
                        style={{ maxHeight: "180px" }}
                      >
                        <Input
                          prefix={<SearchOutlined />}
                          placeholder={t("Search products...")}
                          value={sg.searchQuery}
                          onChange={(e) =>
                            updateSubGroupField(sg.key, "searchQuery", e.target.value)
                          }
                          className="mb-2 text-xs h-8 rounded-md focus:outline-none"
                        />
                        {sg.loading ? (
                          <div className="flex justify-center text-xs py-2">
                            <Spin />
                          </div>
                        ) : sg.products.length > 0 && sg.searchQuery.length > 1 ? (
                          sg.products.map((p) => {
                            const id = p.quant_id ?? p.prod_id ?? p.id;
                            const isSelected = sg.selectedProducts.includes(id);
                            return (
                              <div
                                key={id}
                                onClick={() => toggleSubGroupProduct(sg.key, id)}
                                className={`flex items-center py-1 px-2 rounded-lg cursor-pointer hover:bg-green-50 ${
                                  isSelected ? "bg-green-50" : ""
                                }`}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleSubGroupProduct(sg.key, id);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="ms-2 text-gray-800 text-xs">
                                    {p.product_name2 && p.product_name2 != ""
                                      ? p.product_name2
                                      : p.product_name}
                                  </span>
                                </Checkbox>
                              </div>
                            );
                          })
                        ) : (
                          sg.searchQuery.length > 1 && (
                            <p className="text-center text-gray-400 py-4 text-xs">
                              {t("No products found")}
                            </p>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className={`px-8 py-2 font-semibold text-xs border rounded-lg bg-white text-primaryBlue border-primaryBlue hover:bg-red-600`}
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleSave}
            className={`px-8 py-2 font-semibold text-xs border rounded-lg bg-primaryBlue text-white border-primaryBlue hover:bg-red-600`}
          >
            {t("Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewGroup_Modal;